import React, { useEffect, useState } from 'react';
import useStore from '../../../store/useStore';
import toast from 'react-hot-toast';
import LocationOnOutlinedIcon from '@mui/icons-material/LocationOnOutlined';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import DeleteOutlineOutlinedIcon from '@mui/icons-material/DeleteOutlineOutlined';
import AddOutlinedIcon from '@mui/icons-material/AddOutlined';
import HomeOutlinedIcon from '@mui/icons-material/HomeOutlined';
import WorkOutlineOutlinedIcon from '@mui/icons-material/WorkOutlineOutlined';
import StarOutlinedIcon from '@mui/icons-material/StarOutlined';

const EMPTY_FORM = {
  name: '', email: '', address_type: 'Home', address: '',
  pincode: '', city: '', state: '', latitude: '', longitude: '', is_default: false,
};

const ADDRESS_TYPES = ['Home', 'Work', 'Other'];
const REQUIRED = ['name', 'email', 'address', 'pincode', 'city', 'state'];

const SERVICEABLE_PINCODES = ['411028', '411023'];

const validateField = (name, value) => {
  switch (name) {
    case 'name':
      if (!value?.trim()) return 'Name is required';
      if (value.trim().length < 2) return 'Min 2 characters';
      return '';
    case 'email':
      if (!value?.trim()) return 'Email is required';
      if (!/^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(value.trim())) return 'Enter valid email';
      return '';
    case 'address':
      if (!value?.trim()) return 'Address is required';
      if (value.trim().length < 10) return 'Enter a complete address';
      return '';
    case 'city':
      if (!value?.trim()) return 'City is required';
      if (!/^[a-zA-Z\s]+$/.test(value.trim())) return 'Letters only';
      return '';
    case 'state':
      if (!value?.trim()) return 'State is required';
      if (!/^[a-zA-Z\s]+$/.test(value.trim())) return 'Letters only';
      return '';
    case 'pincode':
      if (!value?.trim()) return 'PIN code is required';
      if (/[^0-9]/.test(value.trim())) return 'Numbers only';
      if (value.trim().length !== 6) return 'Must be 6 digits';
      return '';
    case 'latitude':
      if (String(value ?? '').trim() && isNaN(parseFloat(value))) return 'Enter a valid number';
      return '';
    case 'longitude':
      if (String(value ?? '').trim() && isNaN(parseFloat(value))) return 'Enter a valid number';
      return '';
    default: return '';
  }
};

const TypeIcon = ({ type }) => {
  if (type === 'Work') return <WorkOutlineOutlinedIcon style={{ fontSize: 15 }} />;
  if (type === 'Other') return <LocationOnOutlinedIcon style={{ fontSize: 15 }} />;
  return <HomeOutlinedIcon style={{ fontSize: 15 }} />;
};

/* ── Address Card ── */
const AddressCard = ({ address, onEdit, onDelete, onMakeDefault }) => (
  <div className="group relative flex flex-col rounded-2xl border border-gray-100 bg-white p-5 shadow-sm transition-all duration-200 hover:border-primary-200 hover:shadow-md">
    {/* Default badge */}
    {address.is_default && (
      <div className="absolute right-4 top-4 flex items-center gap-1 rounded-full bg-primary-50 px-2.5 py-1 text-[10px] font-bold text-primary-600">
        <StarOutlinedIcon style={{ fontSize: 10 }} /> Default
      </div>
    )}

    {/* Type chip */}
    <div className="mb-3 flex items-center gap-2">
      <span className="flex h-8 w-8 items-center justify-center rounded-xl bg-primary-50 text-primary-600">
        <TypeIcon type={address.address_type || address.type} />
      </span>
      <span className="text-sm font-bold text-gray-800">
        {address.address_type || address.type || 'Home'}
      </span>
    </div>

    {/* Details */}
    <div className="mb-4 flex-1 space-y-1 text-sm text-gray-600">
      {address.name && <p className="font-semibold text-gray-900">{address.name}</p>}
      {address.email && <p className="text-xs text-gray-400">{address.email}</p>}
      <p className="mt-1 leading-relaxed">{address.address || address.street}</p>
      <p className="text-xs font-medium text-gray-500">
        {address.city}, {address.state} &mdash; {address.pincode}
      </p>
    </div>

    {/* Actions */}
    <div className="flex flex-wrap gap-2 border-t border-gray-50 pt-3">
      {!address.is_default && onMakeDefault && (
        <button
          onClick={() => onMakeDefault(address.id)}
          className="flex flex-1 items-center justify-center gap-1.5 rounded-xl bg-primary-50 py-2 text-xs font-semibold text-primary-600 transition hover:bg-primary-100"
        >
          <StarOutlinedIcon style={{ fontSize: 14 }} /> Make Default
        </button>
      )}
      <button
        onClick={() => onEdit(address)}
        className="flex flex-1 items-center justify-center gap-1.5 rounded-xl bg-gray-50 py-2 text-xs font-semibold text-gray-600 transition hover:bg-primary-50 hover:text-primary-600"
      >
        <EditOutlinedIcon style={{ fontSize: 14 }} /> Edit
      </button>
      <button
        onClick={() => onDelete(address.id)}
        className="flex flex-1 items-center justify-center gap-1.5 rounded-xl bg-gray-50 py-2 text-xs font-semibold text-gray-600 transition hover:bg-red-50 hover:text-red-500"
      >
        <DeleteOutlineOutlinedIcon style={{ fontSize: 14 }} /> Delete
      </button>
    </div>
  </div>
);

/* ── Address Form Modal ── */
const AddressForm = ({ initial = EMPTY_FORM, loading, onSubmit, onCancel, title }) => {
  const [form, setForm] = useState(initial);
  const [errors, setErrors] = useState({});
  const [touched, setTouched] = useState({});
  const [pincodeCityState, setPincodeCityState] = useState({ city: '', state: '' });
  const [pincodeStatus, setPincodeStatus] = useState('idle');

  useEffect(() => {
    setForm(initial);
    setErrors({});
    setTouched({});
    setPincodeCityState({ city: '', state: '' });
    setPincodeStatus('idle');
  }, [initial]); // eslint-disable-line react-hooks/exhaustive-deps

  // Pincode serviceability + auto-fill
  useEffect(() => {
    const pin = form.pincode?.trim();
    if (pin?.length === 6) {
      if (SERVICEABLE_PINCODES.includes(pin)) {
        setPincodeCityState({ city: 'Pune', state: 'Maharashtra' });
        setPincodeStatus('serviceable');
      } else {
        setPincodeCityState({ city: '', state: '' });
        setPincodeStatus('not_serviceable');
      }
    } else {
      setPincodeCityState({ city: '', state: '' });
      setPincodeStatus('idle');
    }
  }, [form.pincode]);

  // Auto-fill city/state from pincode (only if user hasn't manually typed them)
  useEffect(() => {
    if (pincodeCityState.city && !touched.city && !touched.state) {
      setForm((p) => ({ ...p, city: pincodeCityState.city, state: pincodeCityState.state }));
    } else if (!form.pincode || form.pincode.length !== 6) {
      if (!pincodeCityState.city) setForm((p) => ({ ...p, city: '', state: '' }));
    }
  }, [pincodeCityState, form.pincode]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    let clean = value;
    if (name === 'pincode') clean = value.replace(/\D/g, '').slice(0, 6);
    else if (name === 'city' || name === 'state') clean = value.replace(/[^a-zA-Z\s]/g, '');
    const final = type === 'checkbox' ? checked : clean;
    setForm((p) => ({ ...p, [name]: final }));
    if (touched[name]) setErrors((p) => ({ ...p, [name]: validateField(name, final) }));
    if (name === 'pincode' && clean.length !== 6) setPincodeStatus('idle');
  };

  const handleBlur = (e) => {
    const { name, value } = e.target;
    setTouched((p) => ({ ...p, [name]: true }));
    setErrors((p) => ({ ...p, [name]: validateField(name, value) }));
  };

  const handleUseLocation = () => {
    if (!navigator.geolocation) {
      toast.error('Geolocation is not supported by your browser');
      return;
    }
    navigator.geolocation.getCurrentPosition(
      (position) => {
        setForm((p) => ({
          ...p,
          latitude: position.coords.latitude.toFixed(6),
          longitude: position.coords.longitude.toFixed(6),
        }));
        toast.success('Location detected!');
      },
      () => toast.error('Unable to get location. Please allow location access.'),
    );
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    const newErrors = {};
    const newTouched = {};
    REQUIRED.forEach((f) => { newTouched[f] = true; newErrors[f] = validateField(f, form[f] ?? ''); });
    setTouched(newTouched);
    setErrors(newErrors);
    if (Object.values(newErrors).some((e) => e)) return;
    onSubmit(form);
  };

  const ic = (name) =>
    `w-full rounded-xl border px-3 py-2.5 text-sm outline-none transition focus:ring-2 ${
      errors[name] && touched[name]
        ? 'border-red-300 bg-red-50/30 focus:border-red-400 focus:ring-red-100'
        : 'border-gray-200 bg-white focus:border-primary-400 focus:ring-primary-100'
    }`;

  const autoFilled = (name) => pincodeCityState[name] && !touched[name];

  return (
    <div className="fixed inset-0 z-[70] flex items-end justify-center bg-black/40 backdrop-blur-sm sm:items-start sm:pt-[72px] sm:px-4 sm:pb-4">
      <form
        onSubmit={handleSubmit}
        className="w-full max-w-lg flex flex-col rounded-t-3xl bg-white shadow-2xl sm:rounded-3xl"
        style={{ maxHeight: 'calc(100dvh - 80px)', height: 'auto' }}
      >
        {/* Header */}
        <div className="sticky top-0 z-10 flex items-center justify-between border-b border-gray-100 bg-white px-6 py-4">
          <h2 className="text-base font-bold text-gray-900">{title}</h2>
          <button
            type="button"
            onClick={onCancel}
            className="flex h-8 w-8 items-center justify-center rounded-full bg-gray-100 text-gray-500 transition hover:bg-gray-200"
          >
            <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        <div className="flex-1 overflow-y-auto space-y-4 p-6">
          {/* Name + Email */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="mb-1.5 block text-xs font-semibold text-gray-500">Full Name *</label>
              <input name="name" value={form.name} onChange={handleChange} onBlur={handleBlur}
                className={ic('name')} placeholder="Rahul Sharma" />
              {errors.name && touched.name && <p className="mt-1 text-xs text-red-500">{errors.name}</p>}
            </div>
            <div>
              <label className="mb-1.5 block text-xs font-semibold text-gray-500">Email *</label>
              <input name="email" type="email" value={form.email} onChange={handleChange} onBlur={handleBlur}
                className={ic('email')} placeholder="rahul@email.com" />
              {errors.email && touched.email && <p className="mt-1 text-xs text-red-500">{errors.email}</p>}
            </div>
          </div>

          {/* Address Type */}
          <div>
            <label className="mb-1.5 block text-xs font-semibold text-gray-500">Address Type</label>
            <div className="flex gap-2">
              {ADDRESS_TYPES.map((t) => (
                <button key={t} type="button"
                  onClick={() => setForm((p) => ({ ...p, address_type: t }))}
                  className={`flex flex-1 items-center justify-center gap-1.5 rounded-xl border py-2.5 text-xs font-bold transition ${
                    form.address_type === t
                      ? 'border-primary-500 bg-primary-50 text-primary-700'
                      : 'border-gray-200 text-gray-500 hover:border-primary-300 hover:bg-primary-50/50'
                  }`}
                >
                  <TypeIcon type={t} /> {t}
                </button>
              ))}
            </div>
          </div>

          {/* Address */}
          <div>
            <label className="mb-1.5 block text-xs font-semibold text-gray-500">Street Address *</label>
            <textarea name="address" value={form.address} onChange={handleChange} onBlur={handleBlur}
              rows={2} className={ic('address')} placeholder="House no., Building, Street, Area" />
            {errors.address && touched.address && <p className="mt-1 text-xs text-red-500">{errors.address}</p>}
          </div>

          {/* Pincode + City + State */}
          <div className="grid grid-cols-3 gap-3">
            <div>
              <label className="mb-1.5 block text-xs font-semibold text-gray-500">Pincode *</label>
              <input name="pincode" value={form.pincode} onChange={handleChange} onBlur={handleBlur}
                className={`${ic('pincode')} ${
                  pincodeStatus === 'serviceable' ? '!border-emerald-400 !bg-emerald-50/30' :
                  pincodeStatus === 'not_serviceable' ? '!border-red-300 !bg-red-50/30' : ''
                }`}
                placeholder="411001" inputMode="numeric" />
              {errors.pincode && touched.pincode && <p className="mt-1 text-xs text-red-500">{errors.pincode}</p>}
            </div>
            <div>
              <label className="mb-1.5 block text-xs font-semibold text-gray-500">
                City * {autoFilled('city') && <span className="font-normal text-emerald-600">(auto)</span>}
              </label>
              <input name="city" value={form.city} onChange={handleChange} onBlur={handleBlur}
                className={`${ic('city')} ${autoFilled('city') ? '!border-emerald-300 !bg-emerald-50/40' : ''}`}
                placeholder="Pune" />
              {errors.city && touched.city && <p className="mt-1 text-xs text-red-500">{errors.city}</p>}
            </div>
            <div>
              <label className="mb-1.5 block text-xs font-semibold text-gray-500">
                State * {autoFilled('state') && <span className="font-normal text-emerald-600">(auto)</span>}
              </label>
              <input name="state" value={form.state} onChange={handleChange} onBlur={handleBlur}
                className={`${ic('state')} ${autoFilled('state') ? '!border-emerald-300 !bg-emerald-50/40' : ''}`}
                placeholder="Maharashtra" />
              {errors.state && touched.state && <p className="mt-1 text-xs text-red-500">{errors.state}</p>}
            </div>
          </div>

          {/* Pincode status banner — informational only, does not block save */}
          {form.pincode?.length === 6 && pincodeStatus !== 'idle' && (
            <div className={`flex items-center gap-2.5 rounded-xl px-4 py-3 text-sm font-semibold ${
              pincodeStatus === 'serviceable'
                ? 'bg-emerald-50 text-emerald-700'
                : 'bg-amber-50 text-amber-700'
            }`}>
              <span className={`flex h-5 w-5 shrink-0 items-center justify-center rounded-full text-white ${
                pincodeStatus === 'serviceable' ? 'bg-emerald-500' : 'bg-amber-400'
              }`}>
                <svg className="h-3 w-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  {pincodeStatus === 'serviceable'
                    ? <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                    : <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M12 9v4m0 4h.01" />}
                </svg>
              </span>
              {pincodeStatus === 'serviceable'
                ? 'Delivery available — Pune, Maharashtra 🎉'
                : 'Pincode not on our confirmed list — serviceability verified at order time.'}
            </div>
          )}

          {/* Lat + Lng — optional, can be filled via geolocation */}
          <div>
            <div className="mb-1.5 flex items-center justify-between">
              <label className="text-xs font-semibold text-gray-500">GPS Coordinates <span className="font-normal text-gray-400">(optional)</span></label>
              <button
                type="button"
                onClick={handleUseLocation}
                className="flex items-center gap-1 rounded-lg bg-primary-50 px-2.5 py-1 text-[11px] font-bold text-primary-600 transition hover:bg-primary-100"
              >
                <svg className="h-3 w-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                </svg>
                Use My Location
              </button>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <input name="latitude" type="number" step="any" value={form.latitude} onChange={handleChange} onBlur={handleBlur}
                  className={ic('latitude')} placeholder="Latitude (18.5204)" />
                {errors.latitude && touched.latitude && <p className="mt-1 text-xs text-red-500">{errors.latitude}</p>}
              </div>
              <div>
                <input name="longitude" type="number" step="any" value={form.longitude} onChange={handleChange} onBlur={handleBlur}
                  className={ic('longitude')} placeholder="Longitude (73.8567)" />
                {errors.longitude && touched.longitude && <p className="mt-1 text-xs text-red-500">{errors.longitude}</p>}
              </div>
            </div>
          </div>

          {/* Default checkbox */}
          <label className="flex cursor-pointer items-center gap-3 rounded-xl border border-gray-100 bg-gray-50 px-4 py-3 transition hover:bg-primary-50/40">
            <input name="is_default" type="checkbox" checked={form.is_default} onChange={handleChange}
              className="h-4 w-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500" />
            <div>
              <p className="text-sm font-semibold text-gray-800">Set as default address</p>
              <p className="text-xs text-gray-400">Used automatically at checkout</p>
            </div>
          </label>
        </div>

        {/* Footer */}
        <div className="sticky bottom-0 flex gap-3 border-t border-gray-100 bg-white px-6 py-4">
          <button type="button" onClick={onCancel}
            className="flex-1 rounded-xl border border-gray-200 py-3 text-sm font-semibold text-gray-600 transition hover:bg-gray-50">
            Cancel
          </button>
          <button type="submit" disabled={loading}
            className="flex-1 rounded-xl bg-primary-600 py-3 text-sm font-bold text-white shadow-md shadow-primary-600/20 transition hover:bg-primary-700 disabled:cursor-not-allowed disabled:opacity-50">
            {loading ? 'Saving…' : 'Save Address'}
          </button>
        </div>
      </form>
    </div>
  );
};

/* ── Main Section ── */
const AddressesSection = () => {
  const {
    user_addresses, addresses_loading, addresses_error,
    fetchUserAddresses, addAddress, updateAddress, deleteAddress, setDefaultAddress,
  } = useStore();
  const [mode, setMode] = useState(null);
  const [editTarget, setEditTarget] = useState(null);

  useEffect(() => { fetchUserAddresses(); }, [fetchUserAddresses]);

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this address?')) return;
    try { await deleteAddress(id); toast.success('Address deleted'); }
    catch (err) { toast.error(err.message || 'Failed to delete'); }
  };

  const handleEdit = (address) => { setEditTarget(address); setMode('edit'); };

  const handleMakeDefault = async (id) => {
    try { await setDefaultAddress(id); toast.success('Default address updated'); }
    catch (err) { toast.error(err.message || 'Failed to update default'); }
  };

  const handleAddSubmit = async (form) => {
    try { await addAddress(form); toast.success('Address saved!'); setMode(null); }
    catch (err) { toast.error(err.message || 'Failed to add address'); }
  };

  const handleEditSubmit = async (form) => {
    try {
      await updateAddress(editTarget.address_id ?? editTarget.id, form);
      toast.success('Address updated!');
      setMode(null); setEditTarget(null);
    } catch (err) { toast.error(err.message || 'Failed to update address'); }
  };

  const editInitial = editTarget ? {
    name:         editTarget.name         || '',
    email:        editTarget.email        || '',
    address_type: editTarget.address_type || editTarget.type || 'Home',
    address:      editTarget.address      || editTarget.street || '',
    pincode:      editTarget.pincode      || '',
    city:         editTarget.city         || '',
    state:        editTarget.state        || '',
    latitude:     editTarget.latitude     ?? '',
    longitude:    editTarget.longitude    ?? '',
    is_default:   editTarget.is_default   || false,
  } : EMPTY_FORM;

  const defaultAddr = user_addresses.find((a) => a.is_default);
  const otherAddrs  = user_addresses.filter((a) => !a.is_default);

  return (
    <div className="pt-2">
      {/* Header */}
      <div className="mb-6 flex items-center justify-between">
        <div>
          <h1 className="text-xl font-extrabold text-gray-900">My Addresses</h1>
          <p className="mt-0.5 text-sm text-gray-400">Manage your saved delivery locations</p>
        </div>
        <button
          onClick={() => setMode('add')}
          className="flex items-center gap-2 rounded-xl bg-primary-600 px-4 py-2.5 text-sm font-bold text-white shadow-md shadow-primary-600/25 transition hover:bg-primary-700 active:scale-95"
        >
          <AddOutlinedIcon style={{ fontSize: 16 }} /> Add New
        </button>
      </div>

      {/* Loading */}
      {addresses_loading && user_addresses.length === 0 && (
        <div className="flex items-center justify-center py-16">
          <div className="h-8 w-8 animate-spin rounded-full border-4 border-gray-200 border-t-primary-600" />
        </div>
      )}

      {/* Error */}
      {addresses_error && (
        <div className="rounded-2xl border border-red-100 bg-red-50 px-4 py-3 text-sm font-semibold text-red-600">
          {addresses_error}
        </div>
      )}

      {/* Empty state */}
      {!addresses_loading && !addresses_error && user_addresses.length === 0 && (
        <div className="flex flex-col items-center justify-center rounded-2xl border-2 border-dashed border-gray-200 bg-gray-50/60 py-16 text-center">
          <div className="mb-4 flex h-16 w-16 items-center justify-center rounded-2xl bg-primary-50 text-primary-400">
            <LocationOnOutlinedIcon style={{ fontSize: 32 }} />
          </div>
          <p className="font-bold text-gray-700">No addresses yet</p>
          <p className="mt-1 text-sm text-gray-400">Add an address to speed up checkout</p>
          <button
            onClick={() => setMode('add')}
            className="mt-5 flex items-center gap-2 rounded-xl bg-primary-600 px-5 py-2.5 text-sm font-bold text-white shadow-md shadow-primary-600/20 transition hover:bg-primary-700"
          >
            <AddOutlinedIcon style={{ fontSize: 16 }} /> Add Address
          </button>
        </div>
      )}

      {/* Default address */}
      {defaultAddr && (
        <div className="mb-5">
          <p className="mb-2 flex items-center gap-1.5 text-xs font-bold uppercase tracking-wider text-gray-400">
            <span className="h-1.5 w-1.5 rounded-full bg-primary-500" /> Default
          </p>
          <AddressCard address={defaultAddr} onEdit={handleEdit} onDelete={handleDelete} onMakeDefault={handleMakeDefault} />
        </div>
      )}

      {/* Other addresses */}
      {otherAddrs.length > 0 && (
        <div>
          <p className="mb-2 flex items-center gap-1.5 text-xs font-bold uppercase tracking-wider text-gray-400">
            <span className="h-1.5 w-1.5 rounded-full bg-gray-300" /> Other Addresses
          </p>
          <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
            {otherAddrs.map((address) => (
              <AddressCard key={address.id} address={address} onEdit={handleEdit} onDelete={handleDelete} onMakeDefault={handleMakeDefault} />
            ))}
          </div>
        </div>
      )}

      {/* Modals */}
      {mode === 'add' && (
        <AddressForm title="Add New Address" loading={addresses_loading}
          onSubmit={handleAddSubmit} onCancel={() => setMode(null)} />
      )}
      {mode === 'edit' && editTarget && (
        <AddressForm title="Edit Address" initial={editInitial} loading={addresses_loading}
          onSubmit={handleEditSubmit} onCancel={() => { setMode(null); setEditTarget(null); }} />
      )}
    </div>
  );
};

export default AddressesSection;
